/**
 * @deprecated surgeryService.ts — PHASE 1 DISABLED
 *
 * MODULE_SURGERY=false in .env (backend + frontend).
 * Hospital-based surgery workflows are soft-disabled. This service is
 * retained for the ambulatory surgical-procedures module (no longer
 * using the old hospital admission-dependent surgery flow).
 *
 * Endpoint: /api/v1/surgical-procedures (still active for scheduled procedures)
 *
 * Rollback: set VITE_MODULE_SURGERY=true + MODULE_SURGERY=true + config:clear
 */
import apiClient from '../../services/apiClient';

export interface SurgicalProcedure {
  id: number;
  patient_id: number;
  doctor_id: number;
  consultation_id?: number | null;
  procedure_date: string;
  procedure_name: string;
  procedure_type: 'laparoscopic' | 'open' | 'endoscopic' | 'minimally_invasive' | 'other';
  anesthesia_type?: 'general' | 'regional' | 'spinal' | 'local' | 'sedation' | null;
  status: 'scheduled' | 'completed' | 'cancelled';
  diagnosis: string;
  technique?: string | null;
  findings?: string | null;
  complications?: string | null;
  postop_indications?: string | null;
  notes?: string | null;
  duration_minutes?: number | null;
  created_at?: string;
  patient?: { id: number; first_name: string; last_name: string; identification?: string };
  doctor?: { id: number; first_name: string; last_name: string; specialty?: { name: string } };
}

export type CreateSurgicalProcedureRequest = Omit<SurgicalProcedure, 'id' | 'created_at' | 'patient' | 'doctor'>;

interface ApiResponse<T> { success: boolean; data: T; message?: string }
interface PaginatedResponse<T> { data: T[]; total: number; per_page: number; current_page: number; last_page: number }

export const PROCEDURE_TYPE_LABELS: Record<string, string> = {
  laparoscopic: 'Laparoscópica',
  open: 'Abierta',
  endoscopic: 'Endoscópica',
  minimally_invasive: 'Mínimamente Invasiva',
  other: 'Otro',
};

export const ANESTHESIA_LABELS: Record<string, string> = {
  general: 'General',
  regional: 'Regional',
  spinal: 'Raquídea',
  local: 'Local',
  sedation: 'Sedación',
};

export const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Programada',
  completed: 'Completada',
  cancelled: 'Cancelada',
};

export const STATUS_BADGE: Record<string, string> = {
  scheduled: 'custom-badge status-orange',
  completed: 'custom-badge status-green',
  cancelled: 'custom-badge status-pink',
};

export const REQUIREMENT_TYPE = {
  LAB_TEST: 'lab_test',
  LAB_PANEL: 'lab_panel',
  IMAGING: 'imaging',
} as const;

export type RequirementType = (typeof REQUIREMENT_TYPE)[keyof typeof REQUIREMENT_TYPE];

export const REQUIREMENT_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  WAIVED: 'waived',
} as const;

export type RequirementStatus = (typeof REQUIREMENT_STATUS)[keyof typeof REQUIREMENT_STATUS];

export interface SurgicalProcedureRequirement {
  id: number;
  surgical_procedure_id: number;
  requirement_type: RequirementType;
  requirement_id: number;
  requirement_label: string;
  status: RequirementStatus;
  completed_by: { id: number; name: string } | null;
  completed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpdateRequirementStatusRequest {
  status: RequirementStatus;
  notes?: string;
}

export interface ChecklistTemplateItem {
  id: number;
  template_id: number;
  requirement_type: RequirementType;
  requirement_id: number;
  requirement_label: string | null;
  is_required: boolean;
  sort_order: number;
  notes: string | null;
}

export interface ProcedureChecklistTemplate {
  id: number;
  procedure_type: string;
  procedure_name: string | null;
  name: string;
  description: string | null;
  is_active: boolean;
  items: ChecklistTemplateItem[];
  created_at: string;
  updated_at: string;
}

export const REQUIREMENT_TYPE_LABELS: Record<RequirementType, string> = {
  lab_test: 'Laboratorio',
  lab_panel: 'Panel de Laboratorio',
  imaging: 'Imagen',
};

export const REQUIREMENT_STATUS_LABELS: Record<RequirementStatus, string> = {
  pending: 'Pendiente',
  completed: 'Completado',
  waived: 'Omitido',
};

export interface RequirementsSummary {
    completed: number;
    pending: number;
    waived: number;
    total: number;
}

export type AcceptanceStatus = 'ready' | 'attention' | 'urgent' | 'no_requirements';

export interface SurgeonDashboardStats {
    total_this_week: number;
    ready_count: number;
    attention_count: number;
    urgent_count: number;
    completion_rate: number;
}

export interface SurgicalProcedureWithRequirements extends SurgicalProcedure {
    requirements_summary: RequirementsSummary;
    acceptance_status: AcceptanceStatus;
}

export interface SurgeonDashboardResponse {
    stats: SurgeonDashboardStats;
    data: SurgicalProcedureWithRequirements[];
    pagination: {
        current_page: number;
        last_page: number;
        total: number;
    };
}

export interface PostOpMedicationItem {
    medication_name: string;
    dosage: string;
    frequency: string;
    duration: string;
    route: string;
    notes?: string;
    sort_order?: number;
}

export interface PostOpPrescriptionTemplate {
    id: number;
    name: string;
    description: string | null;
    items: PostOpMedicationItem[];
}

export interface PostOpPrescription {
    template_name?: string;
    items: PostOpMedicationItem[];
    generated_at?: string;
}

export interface ConsentItem {
    id?: number;
    type: 'risk' | 'alternative' | 'consequence' | 'note';
    description: string;
    probability?: string;
    sort_order?: number;
}

export interface InformedConsent {
    template_name: string;
    procedure_description: string;
    general_risks: string;
    items: ConsentItem[];
}

export const surgeryService = {
  async getAll(params?: {
    page?: number; per_page?: number; patient_id?: number;
    doctor_id?: number; status?: string; search?: string;
    date_from?: string; date_to?: string;
  }): Promise<PaginatedResponse<SurgicalProcedure>> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') query.append(k, String(v));
      });
    }
    const response = await apiClient.get<ApiResponse<PaginatedResponse<SurgicalProcedure>>>(
      `/surgical-procedures?${query}`
    );
    return response.data.data;
  },

  async getById(id: number): Promise<SurgicalProcedure> {
    const response = await apiClient.get<ApiResponse<SurgicalProcedure>>(`/surgical-procedures/${id}`);
    return response.data.data;
  },

  async create(data: CreateSurgicalProcedureRequest): Promise<SurgicalProcedure> {
    const response = await apiClient.post<ApiResponse<SurgicalProcedure>>('/surgical-procedures', data);
    return response.data.data;
  },

  async update(id: number, data: Partial<CreateSurgicalProcedureRequest>): Promise<SurgicalProcedure> {
    const response = await apiClient.put<ApiResponse<SurgicalProcedure>>(`/surgical-procedures/${id}`, data);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/surgical-procedures/${id}`);
  },

  async getRequirements(procedureId: number): Promise<SurgicalProcedureRequirement[]> {
    const response = await apiClient.get<{ data: SurgicalProcedureRequirement[] }>(
      `/surgical-procedures/${procedureId}/requirements`
    );
    return response.data.data;
  },

  async updateRequirementStatus(
    procedureId: number,
    requirementId: number,
    data: UpdateRequirementStatusRequest
  ): Promise<SurgicalProcedureRequirement> {
    const response = await apiClient.patch<ApiResponse<SurgicalProcedureRequirement>>(
      `/surgical-procedures/${procedureId}/requirements/${requirementId}`,
      data
    );
    return response.data.data;
  },

  async regenerateRequirements(procedureId: number): Promise<SurgicalProcedureRequirement[]> {
    const response = await apiClient.post<ApiResponse<SurgicalProcedureRequirement[]>>(
      `/surgical-procedures/${procedureId}/requirements/regenerate`
    );
    return response.data.data;
  },

  async getChecklistTemplates(): Promise<ProcedureChecklistTemplate[]> {
    const response = await apiClient.get<{ data: ProcedureChecklistTemplate[] }>(
      '/procedure-checklist-templates'
    );
    return response.data.data;
  },

  async generatePostOpPrescription(procedureId: number): Promise<{ template_name: string; items: PostOpMedicationItem[] }> {
    const response = await apiClient.post<ApiResponse<{ template_name: string; items: PostOpMedicationItem[] }>>(
      `/surgical-procedures/${procedureId}/post-op/generate`
    );
    return response.data.data;
  },

  async savePostOpPrescription(procedureId: number, items: PostOpMedicationItem[]): Promise<void> {
    await apiClient.put(`/surgical-procedures/${procedureId}/post-op/save`, { items });
  },

  async getPostOpPrescription(procedureId: number): Promise<PostOpPrescription | null> {
    const response = await apiClient.get<ApiResponse<PostOpPrescription | null>>(
      `/surgical-procedures/${procedureId}/post-op/prescription`
    );
    return response.data.data ?? null;
  },

  async getSurgeonDashboard(params?: {
    from?: string;
    to?: string;
    status?: string;
    page?: number;
    doctor_id?: number;
  }): Promise<SurgeonDashboardResponse> {
    const response = await apiClient.get<ApiResponse<SurgeonDashboardResponse>>('/surgeon-dashboard', {
      params,
    });
    return response.data.data;
  },

  async generateInformedConsent(procedureId: number): Promise<InformedConsent> {
    const response = await apiClient.post<ApiResponse<InformedConsent>>(
      `/surgical-procedures/${procedureId}/informed-consent/generate`
    );
    return response.data.data;
  },
};
